import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  Field,
  Input,
  Button,
} from '@truecare/ui';
import type {
  AdminHomecareDetailResponse,
  AdminHomecareStatusResponse,
  AdminHomecareRetryStripeResponse,
} from '@truecare/schemas';
import { ApiError, unwrap } from '@truecare/api-client';
import { api } from '../../api';
import { actionErrorMessage } from '../../lib/action-error';
import { adminBeforeLoad } from '../../lib/admin-before-load';
import { AdminLayout } from '../../components/AdminLayout';
import { AuditTrailPanel } from '../../components/AuditTrailPanel';
import { ShiftsCard } from '../../components/ShiftsCard';
import { AgreementAcceptancesCard } from '../../components/AgreementAcceptancesCard';

/**
 * Phase 14b-2 — Homecare client detail page.
 *
 * Layout per 14b-2-1: single-scroll with action cards stacked by
 * concern per 14b-2-3 (Identity / Status / Stripe / Recipients) +
 * AuditTrailPanel at the bottom per 14b-6.
 *
 * Every destructive action goes through `useMutation` so the global
 * TanStack Query MutationCache `onError` (wired in main.tsx) catches
 * TotpStepUpRequiredError, opens the step-up modal, and retries the
 * failing mutation per 14b-1-5 FIFO semantics. No raw `fetch` here.
 *
 * On success, invalidate the detail query so the page re-fetches the
 * post-action state (status badge updates, action buttons re-evaluate
 * conditionals, etc.).
 */

export const Route = createFileRoute('/homecare-clients/$id')({
  beforeLoad: ({ params }) => adminBeforeLoad(`/homecare-clients/${params.id}`),
  component: HomecareClientDetailPage,
});

function HomecareClientDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detailKey = ['admin', 'homecare-clients', id] as const;
  const detail = useQuery<AdminHomecareDetailResponse>({
    queryKey: detailKey,
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/homecare-clients/{id}', {
          params: { path: { id } },
        } as never),
      )) as unknown as AdminHomecareDetailResponse,
  });

  function invalidate(): void {
    queryClient.invalidateQueries({ queryKey: detailKey });
  }

  const suspend = useMutation({
    mutationFn: async (reason: string) =>
      (await unwrap(
        api.POST('/api/v1/admin/homecare-clients/{id}/suspend', {
          params: { path: { id } },
          body: { reason },
        } as never),
      )) as unknown as AdminHomecareStatusResponse,
    onSuccess: invalidate,
  });

  const unsuspend = useMutation({
    mutationFn: async () =>
      (await unwrap(
        api.POST('/api/v1/admin/homecare-clients/{id}/unsuspend', {
          params: { path: { id } },
          body: {},
        } as never),
      )) as unknown as AdminHomecareStatusResponse,
    onSuccess: invalidate,
  });

  const terminate = useMutation({
    mutationFn: async (reason: string) =>
      (await unwrap(
        api.POST('/api/v1/admin/homecare-clients/{id}/terminate', {
          params: { path: { id } },
          body: { reason },
        } as never),
      )) as unknown as AdminHomecareStatusResponse,
    onSuccess: invalidate,
  });

  const retryStripe = useMutation({
    mutationFn: async () =>
      (await unwrap(
        api.POST('/api/v1/admin/homecare-clients/{id}/retry-stripe', {
          params: { path: { id } },
        } as never),
      )) as unknown as AdminHomecareRetryStripeResponse,
    onSuccess: invalidate,
  });

  if (detail.isPending) {
    return (
      <AdminLayout>
        <p className="text-sm text-ink-muted">Loading homecare client…</p>
      </AdminLayout>
    );
  }

  if (detail.isError) {
    const status =
      detail.error instanceof ApiError ? detail.error.status : 0;
    return (
      <AdminLayout>
        <p className="text-sm text-danger">
          Failed to load homecare client {status ? `(${status})` : ''}.
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-4"
          onClick={() => navigate({ to: '/homecare-clients' as string })}
        >
          Back to list
        </Button>
      </AdminLayout>
    );
  }

  const c = detail.data;

  return (
    <AdminLayout>
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="font-heading text-3xl text-forest md:text-4xl">
            {c.displayName ?? c.email}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            Homecare client · {c.email}
          </p>
        </div>
        <StatusBadge status={c.status} />
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="mt-2"
        onClick={() => navigate({ to: '/homecare-clients' as string })}
      >
        ← Back to list
      </Button>

      <IdentityCard data={c} />

      <StatusActionsCard
        status={c.status}
        suspendedReason={c.suspensionReason}
        onSuspend={(reason) => suspend.mutateAsync(reason)}
        onUnsuspend={() => unsuspend.mutateAsync()}
        onTerminate={(reason) => terminate.mutateAsync(reason)}
        isSuspendPending={suspend.isPending}
        isUnsuspendPending={unsuspend.isPending}
        isTerminatePending={terminate.isPending}
      />

      <StripeCard
        hasStripeCustomer={c.stripeCustomerId !== null}
        stripeCustomerId={c.stripeCustomerId}
        hasDefaultPaymentMethod={c.hasDefaultPaymentMethod}
        onRetryStripe={() => retryStripe.mutate()}
        isRetryPending={retryStripe.isPending}
        lastRetryResult={retryStripe.data ?? null}
      />

      <RecipientsCard recipients={c.recipients} />

      <ShiftsCard persona="homecare" entityId={c.id} />

      {/* Phase 23 (23f) — signed Client Agreement(s) + legal evidence + PDFs.
          Keyed on the account-holder User id, not the HomecareClient row id. */}
      <AgreementAcceptancesCard userId={c.userId} />

      <Card padding="md" className="mt-6" data-testid="homecare-audit-card">
        <CardHeader title="Audit trail" />
        <AuditTrailPanel
          mode="entity-scoped"
          filters={{
            entityType: 'homecare_client',
            entityId: c.id,
            page: 1,
            limit: 25,
          }}
        />
      </Card>
    </AdminLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components — kept in the same file for grep affinity per the
// existing audit.tsx pattern. Hoist to a sibling file only if they
// grow past ~50 LOC each.
// ─────────────────────────────────────────────────────────────────────

function StatusBadge({
  status,
  testId = 'homecare-status-badge',
}: {
  status: string;
  testId?: string;
}) {
  const tone =
    status === 'ACTIVE'
      ? 'bg-forest/10 text-forest'
      : status === 'SUSPENDED'
        ? 'bg-gold/10 text-gold'
        : 'bg-danger/10 text-danger';
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-semibold ${tone}`}
      data-testid={testId}
    >
      {status}
    </span>
  );
}

function IdentityCard({ data }: { data: AdminHomecareDetailResponse }) {
  return (
    <Card padding="md" className="mt-6" data-testid="homecare-identity-card">
      <CardHeader title="Account" />
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Row label="Display name" value={data.displayName} />
        <Row label="Email" value={data.email} />
        <Row label="Phone" value={data.phone} />
        <Row label="Payment mode" value={data.paymentMode} />
        <Row label="Language" value={data.languagePreference} />
        <Row label="Referral source" value={data.referralSource} />
        <Row
          label="Signed up"
          value={new Date(data.createdAt).toLocaleString('en-CA')}
        />
        <Row
          label="Activated"
          value={
            data.activatedAt
              ? new Date(data.activatedAt).toLocaleString('en-CA')
              : null
          }
        />
      </dl>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-ink">
        {value ?? <span className="text-ink-muted">—</span>}
      </dd>
    </div>
  );
}

function StatusActionsCard(props: {
  status: string;
  suspendedReason: string | null;
  onSuspend: (reason: string) => Promise<unknown>;
  onUnsuspend: () => Promise<unknown>;
  onTerminate: (reason: string) => Promise<unknown>;
  isSuspendPending: boolean;
  isUnsuspendPending: boolean;
  isTerminatePending: boolean;
}) {
  const [suspendReason, setSuspendReason] = useState('');
  const [terminateReason, setTerminateReason] = useState('');
  // H-F2 — surface failures inline; preserve typed reasons on failure.
  const [statusError, setStatusError] = useState<string | null>(null);
  const [suspendError, setSuspendError] = useState<string | null>(null);
  const [terminateError, setTerminateError] = useState<string | null>(null);

  async function runStatus(action: () => Promise<unknown>): Promise<void> {
    setStatusError(null);
    try {
      await action();
    } catch (e) {
      const msg = actionErrorMessage(e);
      if (msg) setStatusError(msg);
    }
  }

  const isTerminated = props.status === 'TERMINATED';
  const isSuspended = props.status === 'SUSPENDED';
  const isActive = props.status === 'ACTIVE';

  return (
    <Card padding="md" className="mt-6" data-testid="homecare-status-card">
      <CardHeader title="Status" />

      <p
        className="mb-4 flex items-center gap-2 text-sm text-ink-muted"
        data-testid="homecare-status-current"
      >
        Current status:{' '}
        <StatusBadge status={props.status} testId="homecare-status-card-badge" />
      </p>

      {isSuspended && props.suspendedReason && (
        <p className="mb-4 rounded-md bg-gold/10 px-3 py-2 text-sm text-gold">
          <strong>Suspended:</strong> {props.suspendedReason}
        </p>
      )}

      {statusError && (
        <p
          className="mb-4 rounded-md bg-danger/10 px-3 py-2 text-sm text-danger"
          data-testid="homecare-status-error"
        >
          {statusError}
        </p>
      )}

      <div className="flex flex-col gap-4">
        {isActive && (
          <form
            className="flex flex-col gap-2 border-l-4 border-gold pl-3"
            onSubmit={(e) => {
              e.preventDefault();
              const reason = suspendReason.trim();
              if (reason.length === 0) return;
              setSuspendError(null);
              props.onSuspend(reason).then(
                () => setSuspendReason(''),
                (err: unknown) => {
                  const msg = actionErrorMessage(err);
                  if (msg) setSuspendError(msg);
                },
              );
            }}
          >
            <Field label="Suspension reason (visible to operator only)">
              <Input
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="e.g. AR delinquency 60+ days"
                data-testid="homecare-suspend-reason"
              />
            </Field>
            {suspendError && (
              <p className="text-sm text-danger" data-testid="homecare-suspend-error">
                {suspendError}
              </p>
            )}
            <div>
              <Button
                type="submit"
                size="sm"
                variant="secondary"
                disabled={suspendReason.trim().length === 0}
                loading={props.isSuspendPending}
                data-testid="homecare-suspend-button"
              >
                Suspend account
              </Button>
            </div>
          </form>
        )}

        {isSuspended && (
          <div>
            <Button
              size="sm"
              variant="secondary"
              loading={props.isUnsuspendPending}
              onClick={() => void runStatus(props.onUnsuspend)}
              data-testid="homecare-unsuspend-button"
            >
              Unsuspend account
            </Button>
          </div>
        )}

        {!isTerminated && (
          <form
            className="flex flex-col gap-2 border-l-4 border-danger pl-3"
            onSubmit={(e) => {
              e.preventDefault();
              const reason = terminateReason.trim();
              if (reason.length === 0) return;
              setTerminateError(null);
              props.onTerminate(reason).then(
                () => setTerminateReason(''),
                (err: unknown) => {
                  const msg = actionErrorMessage(err);
                  if (msg) setTerminateError(msg);
                },
              );
            }}
          >
            <Field label="Termination reason (irreversible)">
              <Input
                value={terminateReason}
                onChange={(e) => setTerminateReason(e.target.value)}
                placeholder="e.g. account holder deceased, reported fraud"
                data-testid="homecare-terminate-reason"
              />
            </Field>
            {terminateError && (
              <p className="text-sm text-danger" data-testid="homecare-terminate-error">
                {terminateError}
              </p>
            )}
            <div>
              <Button
                type="submit"
                size="sm"
                variant="danger"
                disabled={terminateReason.trim().length === 0}
                loading={props.isTerminatePending}
                data-testid="homecare-terminate-button"
              >
                Terminate account
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}

function StripeCard(props: {
  hasStripeCustomer: boolean;
  stripeCustomerId: string | null;
  hasDefaultPaymentMethod: boolean;
  onRetryStripe: () => void;
  isRetryPending: boolean;
  lastRetryResult: AdminHomecareRetryStripeResponse | null;
}) {
  return (
    <Card padding="md" className="mt-6" data-testid="homecare-stripe-card">
      <CardHeader title="Stripe" />
      <dl className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Row
          label="Stripe customer"
          value={props.stripeCustomerId ?? null}
        />
        <Row
          label="Default payment method"
          value={props.hasDefaultPaymentMethod ? 'On file' : 'None'}
        />
      </dl>

      {!props.hasStripeCustomer && (
        <div>
          <p className="mb-2 text-sm text-ink-muted">
            Stripe customer was never created (signup soft-degraded when
            Stripe was unconfigured). Retry once Stripe is configured.
          </p>
          <Button
            size="sm"
            variant="secondary"
            loading={props.isRetryPending}
            onClick={props.onRetryStripe}
            data-testid="homecare-retry-stripe-button"
          >
            Retry Stripe customer create
          </Button>
        </div>
      )}

      {props.lastRetryResult && (
        <p
          className={`mt-3 text-sm ${
            props.lastRetryResult.ok ? 'text-forest' : 'text-danger'
          }`}
          data-testid="homecare-retry-stripe-result"
        >
          {props.lastRetryResult.ok
            ? `Created Stripe customer ${props.lastRetryResult.stripeCustomerId}.`
            : `Retry failed: ${props.lastRetryResult.message ?? 'Unknown error'}`}
        </p>
      )}
    </Card>
  );
}

function RecipientsCard({
  recipients,
}: {
  recipients: AdminHomecareDetailResponse['recipients'];
}) {
  return (
    <Card padding="md" className="mt-6" data-testid="homecare-recipients-card">
      <CardHeader
        title={`Recipients (${recipients.length})`}
        description="People at this account receiving care. Read-only; account holder manages additions on portal."
      />
      {recipients.length === 0 ? (
        <p className="text-sm text-ink-muted">No recipients on file.</p>
      ) : (
        <ul className="divide-y divide-border">
          {recipients.map((r) => (
            <li key={r.id} className="py-3" data-testid="homecare-recipient-row">
              <div className="flex items-baseline justify-between">
                <span className="font-semibold text-ink">{r.name}</span>
                {r.archivedAt && (
                  <span className="text-xs uppercase tracking-wide text-ink-muted">
                    Archived
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-sm text-ink-muted">
                Relationship: {r.relationshipToAccountHolder}
              </p>
              <p className="mt-0.5 text-xs text-ink-muted">
                {r.serviceAddressLine1}
                {r.serviceAddressLine2 ? `, ${r.serviceAddressLine2}` : ''},{' '}
                {r.serviceAddressCity}, {r.serviceAddressProvince}{' '}
                {r.serviceAddressPostalCode}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
